import type { EmploymentContract, PolicyVerdict } from '@squadlab/contracts';
import { KSA_POLICY } from './tables.js';

/**
 * Probation window check (art. 53): the agreed probation may not exceed 90
 * days, extendable to 180 by written agreement. Pure; the verdict carries the
 * computed window so gates can show the numbers.
 */
const MS_PER_DAY = 86_400_000;

export function probationCheck(contract: EmploymentContract): PolicyVerdict {
  const t = KSA_POLICY.probation;
  if (!contract.probationEndDate) {
    return {
      allowed: true,
      ruleRef: t.ruleRef,
      explanation: 'No probation period agreed — nothing to check.',
    };
  }
  const start = new Date(`${contract.startDate}T00:00:00Z`).getTime();
  const end = new Date(`${contract.probationEndDate}T00:00:00Z`).getTime();
  const days = Math.round((end - start) / MS_PER_DAY);

  if (days < 0) {
    return {
      allowed: false,
      ruleRef: t.ruleRef,
      explanation: 'Probation end date precedes the contract start date.',
      computed: { probationDays: days },
    };
  }
  if (days <= t.maxDays) {
    return {
      allowed: true,
      ruleRef: t.ruleRef,
      explanation: `Probation of ${days} days is within the ${t.maxDays}-day standard window.`,
      computed: { probationDays: days, maxDays: t.maxDays },
    };
  }
  if (days <= t.extendedMaxDays) {
    return {
      allowed: true,
      ruleRef: t.ruleRef,
      explanation: `Probation of ${days} days exceeds ${t.maxDays} days — valid only with a written extension agreement (max ${t.extendedMaxDays}).`,
      computed: { probationDays: days, maxDays: t.maxDays, extendedMaxDays: t.extendedMaxDays },
    };
  }
  return {
    allowed: false,
    ruleRef: t.ruleRef,
    explanation: `Probation of ${days} days exceeds the ${t.extendedMaxDays}-day legal maximum.`,
    computed: { probationDays: days, extendedMaxDays: t.extendedMaxDays },
  };
}
